import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Bell, Check, X, Briefcase, Wallet, Calendar, Shield, ArrowRight } from 'lucide-react';
import axios from 'axios';

// ── Kind derivation ──────────────────────────────────────────────────────────

function getKind(n) {
    const status = n.application?.status;
    switch (n.type) {
        case 'APPLICATION_UPDATE':
            if (status === 'ACCEPTED') return 'accepted';
            if (status === 'REJECTED') return 'declined';
            return 'application';
        case 'NEW_APPLICATION': return 'application';
        case 'JOB_CANCELLED':  return 'declined';
        case 'PAYMENT_UPDATE': return 'payout';
        case 'JOB_ALERT':      return 'reminder';
        case 'SYSTEM_MESSAGE': return 'system';
        default: return 'system';
    }
}

const KIND_META = {
    accepted:    { Icon: Check,     accent: 'teal',   label: 'Accepted' },
    declined:    { Icon: X,         accent: 'coral',  label: 'Declined' },
    application: { Icon: Briefcase, accent: 'amber',  label: 'New applicant' },
    payout:      { Icon: Wallet,    accent: 'teal',   label: 'Payout' },
    reminder:    { Icon: Calendar,  accent: 'amber',  label: 'Reminder' },
    system:      { Icon: Shield,    accent: 'neutral',label: 'System' },
};

function getTitle(kind, n) {
    const job = n.application?.job?.title;
    // JOB_CANCELLED maps to 'declined' kind but needs its own title
    if (n.type === 'JOB_CANCELLED') return job ? `Job cancelled — ${job}` : 'Job cancelled';
    switch (kind) {
        case 'accepted':    return job ? `You got the job — ${job}` : 'Application accepted';
        case 'declined':    return job ? `Application declined — ${job}` : 'Application declined';
        case 'application': return job ? `New applicant on ${job}` : 'New applicant';
        case 'payout':      return 'Balance updated';
        case 'reminder':    return job ? `Upcoming shift: ${job}` : 'Shift reminder';
        default:            return 'Notification';
    }
}

function getBody(kind, n) {
    if (n.message) return n.message;
    const job = n.application?.job?.title ?? 'your job';
    switch (kind) {
        case 'accepted': return `You've been accepted for "${job}". Check the details and get ready.`;
        case 'declined': return `Your application for "${job}" wasn't taken forward this time.`;
        default:         return '';
    }
}

// ── Date helpers ──────────────────────────────────────────────────────────────

function groupLabel(iso) {
    const d   = new Date(iso);
    const now = new Date();
    const today     = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today - 86400000);
    if (d >= today)     return 'Today';
    if (d >= yesterday) return 'Yesterday';
    const weekAgo = new Date(today - 6 * 86400000);
    if (d >= weekAgo) return d.toLocaleDateString('en-GB', { weekday: 'long' });
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

function relativeTime(iso) {
    const diff = Date.now() - new Date(iso);
    const mins  = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days  = Math.floor(diff / 86400000);
    if (mins  < 1)  return 'just now';
    if (mins  < 60) return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days  < 7)  return `${days}d ago`;
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

// ── Filter config ─────────────────────────────────────────────────────────────

const FILTERS = [
    { id: 'all',    label: 'All' },
    { id: 'unread', label: 'Unread' },
    { id: 'jobs',   label: 'Jobs',   kinds: ['accepted', 'declined', 'reminder'] },
    { id: 'money',  label: 'Money',  kinds: ['payout'] },
    { id: 'system', label: 'System', kinds: ['system'] },
];

function getToken() {
    return localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
}

// ── Row component ─────────────────────────────────────────────────────────────

function NRow({ n, onMark, onDismiss }) {
    const kind = getKind(n);
    const meta = KIND_META[kind] ?? KIND_META.system;
    const { Icon } = meta;
    const jobId = n.application?.job?.id;

    return (
        <article className={`nt-row nt-row-${meta.accent}${n.read ? '' : ' is-unread'}`}>
            {!n.read && <span className="nt-row-unread-dot" aria-label="Unread" />}

            <div className="nt-row-icon"><Icon size={16} /></div>

            <div className="nt-row-body">
                <div className="nt-row-meta">
                    <span className="nt-row-kind">{meta.label}</span>
                    <span>·</span>
                    <span>{relativeTime(n.createdAt)}</span>
                </div>
                <h4 className="nt-row-title">{getTitle(kind, n)}</h4>
                <p className="nt-row-text">{getBody(kind, n)}</p>
                {jobId && (
                    <Link to={`/get-job/${jobId}`} className="nt-row-cta">
                        View job <ArrowRight size={11} />
                    </Link>
                )}
            </div>

            <div className="nt-row-actions">
                {!n.read && (
                    <button className="nt-row-btn" onClick={onMark} title="Mark as read" aria-label="Mark as read">
                        <Check size={14} />
                    </button>
                )}
                <button className="nt-row-btn" onClick={onDismiss} title="Dismiss" aria-label="Dismiss">
                    <X size={14} />
                </button>
            </div>
        </article>
    );
}

// ── Main component ────────────────────────────────────────────────────────────

const NotificationsPage = () => {
    const [items, setItems]   = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');

    const load = useCallback(async () => {
        const token = getToken();
        if (!token) { setLoading(false); return; }
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_API_BASE_URL}/api/notifications`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            // Normalise field name: backend sends `isRead` (boolean getter = `read` in JSON after Jackson)
            setItems(res.data ?? []);
        } catch (e) {
            console.error('Failed to load notifications', e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const markOne = async (id) => {
        const token = getToken();
        try {
            await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/api/notifications/${id}/mark-as-read`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setItems(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
        } catch (e) { console.error(e); }
    };

    const markAll = async () => {
        const token = getToken();
        try {
            await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/api/notifications/mark-all-as-read`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setItems(prev => prev.map(n => ({ ...n, read: true })));
        } catch (e) { console.error(e); }
    };

    const dismiss = async (id) => {
        const token = getToken();
        try {
            await axios.delete(
                `${process.env.REACT_APP_API_BASE_URL}/api/notifications/${id}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setItems(prev => prev.filter(n => n.id !== id));
        } catch (e) { console.error(e); }
    };

    // ── Derived state ──
    const unread = items.filter(n => !n.read).length;

    const filtered = items.filter(n => {
        if (filter === 'all')    return true;
        if (filter === 'unread') return !n.read;
        const f = FILTERS.find(x => x.id === filter);
        return f?.kinds?.includes(getKind(n)) ?? true;
    });

    // Group by date preserving order
    const groups = [];
    filtered.forEach(n => {
        const label = groupLabel(n.createdAt);
        const last  = groups[groups.length - 1];
        if (last && last.label === label) last.items.push(n);
        else groups.push({ label, items: [n] });
    });

    const filterCounts = FILTERS.reduce((acc, f) => {
        if (f.id === 'all')    acc[f.id] = items.length;
        else if (f.id === 'unread') acc[f.id] = unread;
        else acc[f.id] = items.filter(n => f.kinds?.includes(getKind(n))).length;
        return acc;
    }, {});

    if (loading) return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', color: 'var(--ink-500)', fontFamily: 'var(--font-mono)', fontSize: 13 }}>
            Loading notifications…
        </div>
    );

    return (
        <div className="nt-page">
            <main className="nt-main">
                <div className="nt-container">

                    {/* ── Header ── */}
                    <header className="nt-head">
                        <div>
                            <span className="eyebrow">Inbox</span>
                            <h1 className="nt-title">
                                Notifications
                                {unread > 0 && <span className="nt-title-badge">{unread} new</span>}
                            </h1>
                            <p className="nt-sub">A running log of everything happening across your jobs and applications.</p>
                        </div>
                        <div>
                            <button
                                className="ix-btn ix-btn-outline ix-btn-sm"
                                onClick={markAll}
                                disabled={unread === 0}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                            >
                                <Check size={12} /> Mark all as read
                            </button>
                        </div>
                    </header>

                    {/* ── Filter tabs ── */}
                    <nav className="nt-filters" role="tablist">
                        {FILTERS.map(f => (
                            <button
                                key={f.id}
                                role="tab"
                                aria-selected={filter === f.id}
                                className={`nt-filter${filter === f.id ? ' is-on' : ''}`}
                                onClick={() => setFilter(f.id)}
                            >
                                <span>{f.label}</span>
                                <span className="nt-filter-n">{filterCounts[f.id] ?? 0}</span>
                            </button>
                        ))}
                    </nav>

                    {/* ── Content ── */}
                    {groups.length === 0 ? (
                        <div className="nt-empty">
                            <div className="nt-empty-icon"><Bell size={26} /></div>
                            <h3 className="nt-empty-title">All caught up.</h3>
                            <p style={{ color: 'var(--ink-500)', fontSize: 14 }}>Nothing new in this filter.</p>
                        </div>
                    ) : groups.map(g => (
                        <section key={g.label} className="nt-group">
                            <h2 className="nt-group-title">{g.label}</h2>
                            <div className="nt-list">
                                {g.items.map(n => (
                                    <NRow
                                        key={n.id}
                                        n={n}
                                        onMark={() => markOne(n.id)}
                                        onDismiss={() => dismiss(n.id)}
                                    />
                                ))}
                            </div>
                        </section>
                    ))}

                </div>
            </main>
        </div>
    );
};

export default NotificationsPage;
